
import React, { useState, useContext, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArticleIcon from "@mui/icons-material/Article";
import { AppStateContext } from "../../App";
import axios from "axios";
import baseURL from "../../config/config";

const axiosInstance = axios.create({
  baseURL: baseURL,
});

const PassageWordMapping= () => {
  const { access_token } = useContext(AppStateContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      setError("Invalid file type. Please upload an Excel file (.xlsx, .xls).");
      setSelectedFile(null);
      return false;
    }
    return true;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      setError("");
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDownload = async () => {
    if (!selectedFile) {
      setError("Please upload an Excel file with textbook titles to proceed.");
      return;
    }

    if (!access_token) {
      setError("You are not logged in. Please log in to download the report.");
      return;
    }

    setLoading(true);
    setError("");

    const formData = new FormData();
    formData.append("format", "excel");
    formData.append("textbook_list", selectedFile);

    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;

    try {
      const response = await axiosInstance.post(
        "/api/trail/reading-content-word-mapping/",
        formData,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);

      link.href = url;
      link.setAttribute("download", `word-mapping-analysis-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);

    } catch (err) {
      console.error("Download failed:", err);
      setError("Failed to generate the report. Please check your file or try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
        backgroundColor: "#f4f6f8",
        padding: 3,
      }}
    >
      <Card sx={{ maxWidth: 500, width: "100%", boxShadow: 3, borderRadius: 2 }}>
        <CardContent sx={{ padding: "32px !important" }}>
          <Stack spacing={3} alignItems="center">
            <DownloadIcon color="primary" sx={{ fontSize: 50 }} />

            <Typography variant="h5" component="h1" fontWeight="bold">
              Generate Mapping Report
            </Typography>

            <Typography variant="body1" color="text.secondary" textAlign="center">
              Upload an Excel file with textbook titles in the first column to generate a report.
            </Typography>

            {error && <Alert severity="error" sx={{ width: "100%" }}>{error}</Alert>}

            {/* Drag & Drop + Click Upload Box */}
            <Box
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              sx={{
                border: "2px dashed #1976d2",
                padding: "20px",
                borderRadius: "8px",
                width: "100%",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: "#fafafa",
                "&:hover": { backgroundColor: "#f0f6ff" },
              }}
            >
              <Typography variant="body1" sx={{ mb: 1 }}>
                Drag & drop Excel file here, or click to browse
              </Typography>

              <UploadFileIcon color="primary" sx={{ fontSize: 40 }} />

              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept=".xlsx, .xls"
                onChange={handleFileChange}
              />
            </Box>

            {selectedFile && (
              <Chip
                label={selectedFile.name}
                onDelete={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                color="primary"
                variant="outlined"
              />
            )}

            <Button
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={20} color="inherit" /> : <ArticleIcon />
              }
              onClick={handleDownload}
              disabled={!selectedFile || loading}
              fullWidth
              size="large"
              sx={{ mt: 2 }}
            >
              {loading ? "Generating Report..." : "Generate & Download Report"}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PassageWordMapping;